import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense, useContext, useEffect } from 'react';
import { matchPath } from 'react-router';
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FirebaseAppContext } from 'libs.firebase_react';
import { Spinner } from 'libs.nucleus';
import { NotFoundPage, PublicRoute } from 'libs.react';
import { AuthContext, AuthRoute, ForgotPasswordPage, LoginPage, MfaChallengePage, MfaEnrollmentPage, ResetPasswordPage, SwitchWorkspacePage, } from 'libs.react_internal';
import { useSelfServiceNavigation } from '../../hooks';
import { BuildInfo } from '../build_info';
import { PrivateRoute } from '../routes';
// Whitelist of routes that are accessible without authentication
const routeWhitelist = [
    AuthRoute.LOGIN,
    AuthRoute.MFA_CHALLENGE,
    AuthRoute.MFA_SETUP,
    AuthRoute.FORGOT_PASSWORD,
    AuthRoute.RESET_PASSWORD,
    PublicRoute.VERSION,
];
export const Router = () => {
    const { appRoutes, defaultAppSection } = useSelfServiceNavigation();
    const { isLoggedIn, navigateToLogin, redirectToDefaultPage, user } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { logEvent, setAnalyticsUserId } = useContext(FirebaseAppContext);
    const [searchParams] = useSearchParams();
    useEffect(() => {
        setAnalyticsUserId(user ? user.path : null);
        if (!isLoggedIn) {
            for (const route of routeWhitelist) {
                if (matchPath({ path: route, caseSensitive: false, end: false }, location.pathname)) {
                    return;
                }
            }
            navigateToLogin();
        }
        else {
            // We need to redirect to the default page if the user is already logged in and has the location param
            if (location.pathname === '/' && searchParams.has('location')) {
                redirectToDefaultPage();
            }
            else if (location.pathname === '/' && defaultAppSection.externalUrl && defaultAppSection.redirectTo) {
                window.location.href = defaultAppSection.redirectTo;
            }
        }
    }, [isLoggedIn]);
    useEffect(() => {
        logEvent('page_view', { page_location: location.pathname });
    }, [location]);
    return (_jsx(Suspense, { fallback: _jsx(Spinner, { size: 'full' }), children: _jsxs(Routes, { children: [_jsx(Route, { path: '/', element: _jsx(Navigate, { to: defaultAppSection.path }) }), _jsx(Route, { path: AuthRoute.LOGIN, element: _jsx(LoginPage, {}) }), _jsx(Route, { path: AuthRoute.FORGOT_PASSWORD, element: _jsx(ForgotPasswordPage, {}) }), _jsx(Route, { path: AuthRoute.RESET_PASSWORD, element: _jsx(ResetPasswordPage, {}) }), _jsx(Route, { path: AuthRoute.MFA_CHALLENGE, element: _jsx(MfaChallengePage, {}) }), _jsx(Route, { path: AuthRoute.MFA_SETUP, element: _jsx(MfaEnrollmentPage, {}) }), _jsx(Route, { path: AuthRoute.SWITCH_WORKSPACE_ID, element: _jsx(SwitchWorkspacePage, {}) }), _jsx(Route, { path: PublicRoute.VERSION, element: _jsx(BuildInfo, {}) }), Object.values(appRoutes).map((appsSection) => (_jsxs(Route, { path: appsSection.path, element: _jsx(PrivateRoute, { sectionPath: appsSection.path, navItems: !appsSection.hideSideNav && appsSection.appRoutes }), children: [_jsx(Route, { path: appsSection.path, element: _jsx(Navigate, { to: appsSection.redirectTo || appsSection.appRoutes[0].path }) }), appsSection.appRoutes.map(({ path, component }) => component && _jsx(Route, { path: `${path}/*`, Component: component }, path))] }, appsSection.path))), _jsx(Route, { path: '*', element: _jsx(PrivateRoute, { sectionPath: '*' }), children: _jsx(Route, { path: '*', element: _jsx(NotFoundPage, { goBack: () => navigate(-1) }) }) })] }) }));
};
